import "reflect-metadata";

import {
  type ArrayOrValue,
  type DocIdOfTModel,
  type DocModelOfTModel,
  type DotPrefix,
  type ErrorCallback,
  getCollectionName,
  type NormalizeModel,
  type OmitDeclFields,
  type OrderByDirection,
  type Reference,
  type SubCollectionIdOfTModel,
  type SubCollectionOfTMModel,
  type Timestamp,
  type TModelCtor,
  type Unsubscribe,
  type WhereFilterOp,
} from "@doitintl/models-types";
import { type AppRouter } from "@doitintl/mongo-data-api-handlers";
import {
  buildCollectionPath,
  buildDocumentPath,
  type BulkDataRquest,
  type BulkWriteModel,
  type CollectionName,
  type CollectionPath,
  convertCollectionName,
  convertDocumentId,
  convertDocumentPath,
  type DocumentChangeType,
  type DocumentId,
  type DocumentPath,
  type MongoNativeReference,
  parseCollectionPath,
  parseDocumentPath,
  type SetOptions,
} from "@doitintl/mongo-types";
import { createTRPCClient, httpLink, type TRPCClient, type TRPCLink } from "@trpc/client";
import EJSON from "ejson";
import stableStringify from "json-stable-stringify";
import get from "lodash/get";
import isPlainObject from "lodash/isPlainObject";
import type { IsAny, ReadonlyDeep, UnionToIntersection } from "type-fest";

import { type DocumentData, FieldValue } from "./firemongo/common";
import { TypeUtils } from "./firemongo/convertors";
import { FieldPath } from "./firemongo/path";
import {
  and,
  type AndQueryCompositeFilterConstraint,
  or,
  type OrQueryCompositeFilterConstraint,
  type QueryCompositeFilterConstraint,
  type QueryConstraint,
  type QueryFieldFilterConstraint,
  type QueryFilterConstraint,
  where,
} from "./firemongo/query";
import { MongoTimestamp } from "./firemongo/timestamp";
import { getTracer } from "./Tracer";

type TrpcClient = TRPCClient<AppRouter>;

const operatorMap = {
  "==": "$eq",
  "!=": "$ne",
  ">": "$gt",
  ">=": "$gte",
  "<": "$lt",
  "<=": "$lte",
  in: "$in",
  "not-in": "$nin",
  "array-contains": "$eq",
  "array-contains-any": "$in", // for arrays
} as const;

function transformToMongoQuery(inputs: Array<QueryFilterConstraint>): any {
  let result = {};

  const singal = (input: QueryFilterConstraint) => {
    if (input.type === "where") {
      const mongoOp = operatorMap[input.op];

      return { [input.field]: { [mongoOp]: ConvertModelToMongo.encodeValSingle(input.value) } };
    }

    if (input.type === "or") {
      return { $or: (input as OrQueryCompositeFilterConstraint).filters.map(singal) };
    }

    return { $and: (input as AndQueryCompositeFilterConstraint).filters.map(singal) };
  };

  for (const input of inputs) {
    const query = singal(input);

    result = { ...result, ...query };
  }

  return result;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class ConvertModelToMongo {
  static encodeValSingle(val: unknown): any {
    if (val instanceof FieldValue) {
      throw new Error(`Unsupported FieldValue: ${val.methodName}`);
    }

    if (val instanceof MongoModelReference) {
      return val.toMongoRef();
    }

    if (Array.isArray(val)) {
      return val.map((v: unknown) => ConvertModelToMongo.encodeValSingle(v));
    }

    if (val instanceof Date) {
      return { $date: val.toISOString() };
    }

    if (val instanceof MongoTimestamp) {
      const totalMillis = val.seconds * 1000 + val.nanoseconds / 1000000;
      const date = new Date(totalMillis);
      return { $date: date.toISOString() };
    }

    if (isPlainObject(val)) {
      const objectResult = {};
      for (const [field, fieldVal] of Object.entries(val as Record<string, unknown>)) {
        objectResult[field] = ConvertModelToMongo.encodeValSingle(fieldVal);
      }

      return objectResult;
    }

    return val;
  }
}

class ModelConverter {
  constructor(private readonly apiClient: MongoClient) {}

  private static fieldName = (parentField: string, field: string): string =>
    `${parentField}${parentField ? "." : ""}${field}`;

  toMongoData(data: DocumentData, atomic: boolean): any {
    function encodeVal(
      val: unknown,
      field: string,
      result: Record<string, unknown>,
      parentPath: string,
      parent: Record<string, unknown>
    ): void {
      if (val instanceof MongoModelReference) {
        result[field] = val.toMongoRef();
        return;
      }

      if (Array.isArray(val)) {
        result[field] = val.map((v) => ConvertModelToMongo.encodeValSingle(v));
        return;
      }

      if (val instanceof Date) {
        result[field] = { $date: val.toISOString() };
        return;
      }

      if (val instanceof FieldValue) {
        if (val.isEqual(FieldValue.serverTimestamp())) {
          if (!parent.$currentDate) {
            parent.$currentDate = {};
          }
          (parent as Record<string, boolean>).$currentDate[ModelConverter.fieldName(parentPath, field)] = true;
          return;
        }

        const operators = [
          {
            check: () => FieldValue.arrayUnion(),
            op: "$addToSet",
            arrayOp: "$each",
            isArray: true,
          },
          {
            check: () => FieldValue.arrayRemove(),
            op: "$pull",
            arrayOp: "$in",
            isArray: true,
          },
          {
            check: () => FieldValue.increment(),
            op: "$inc",
            arrayOp: undefined,
            isArray: false,
          },
        ] as const;

        for (const { check, op, arrayOp, isArray } of operators) {
          if (val.isEqual(check()) && val.elements) {
            if (!parent[op]) {
              parent[op] = {};
            }

            const encoded = isArray
              ? val.elements.map((v: unknown) => ConvertModelToMongo.encodeValSingle(v))
              : ConvertModelToMongo.encodeValSingle(val.elements[0]);

            const encodedOp = arrayOp ? { [arrayOp]: encoded } : encoded;

            (parent as Record<string, boolean>)[op][ModelConverter.fieldName(parentPath, field)] = encodedOp;
            return;
          }
        }

        throw new Error(`Unsupported FieldValue: ${val.methodName}`);
      }

      if (val instanceof MongoTimestamp) {
        const totalMillis = val.seconds * 1000 + val.nanoseconds / 1000000;
        const date = new Date(totalMillis);
        result[field] = { $date: date.toISOString() };
        return;
      }

      if (isPlainObject(val)) {
        const objectResult = {};
        TypeUtils.visitObj(objectResult, val, encodeVal, ModelConverter.fieldName(parentPath, field), parent);
        result[field] = objectResult;
        return;
      }

      result[field] = val;
    }

    const result: Record<string, unknown> = {};
    const storageResult: Record<string, unknown> = atomic ? {} : result;
    TypeUtils.visitObj(storageResult, data, encodeVal, "", result);

    if (atomic && Object.keys(storageResult).length > 0) {
      result.$set = storageResult;
    }

    return result;
  }

  fromMongoData<TModel extends DocumentData, IDField extends string = "", RefField extends string = "">(
    data: Readonly<DocumentData>
  ): ModelData<TModel, IDField, RefField> {
    const decodeValSingle = (val: unknown): any => {
      if (TypeUtils.isDBRef(val)) {
        const path = `${val.$ref}/${val.$id}`;
        return this.apiClient.doc(path);
      }

      if (TypeUtils.isDBDate(val)) {
        return MongoTimestamp.fromDate(new Date(val.$date));
      }

      if (val instanceof Date) {
        return new MongoTimestamp(val.getTime() / 1000, val.getTime() % 1000);
      }

      if (Array.isArray(val)) {
        return val.map((v) => decodeValSingle(v));
      }

      if (isPlainObject(val)) {
        const objectResult = {};
        TypeUtils.visitObj(objectResult, val, decodeVal, "", data);
        return objectResult;
      }

      return val;
    };

    const decodeVal = (
      val: unknown,
      field: string,
      result: Record<string, unknown>,
      _parentPath: string,
      _parent: Record<string, unknown>
    ): any => {
      if (TypeUtils.isDBRef(val)) {
        const path = `${val.$ref}/${val.$id}`;
        result[field] = this.apiClient.doc(path);
        return;
      }

      if (TypeUtils.isDBDate(val)) {
        result[field] = MongoTimestamp.fromDate(new Date(val.$date));
        return;
      }

      if (val instanceof Date) {
        result[field] = new MongoTimestamp(val.getTime() / 1000, val.getTime() % 1000);
        return;
      }

      if (Array.isArray(val)) {
        result[field] = val.map((v) => decodeValSingle(v));
        return;
      }

      if (isPlainObject(val)) {
        const objectResult = {};
        TypeUtils.visitObj(objectResult, val, decodeVal, field, data);
        result[field] = objectResult;
        return;
      }

      result[field] = val;
    };

    const result = {};
    TypeUtils.visitObj(result, data, decodeVal, "", data);

    return result as ModelData<TModel, IDField, RefField>;
  }
}

const logEJSONparse = (data: string) => {
  try {
    return EJSON.parse(data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`invalid ejson ${data}`, error);
    throw error;
  }
};

const httpBatchLinkWithPrefix = (url: string) =>
  httpLink(url === "/" ? { url: "/api/mongo/trpc" } : { url: new URL("/api/mongo/trpc", url) });

class MongoClientImplClient {
  private readonly trpcClientFactory: () => TrpcClient;

  private readonly mongoDataApiUrl: string;

  private readonly dataSource: string;

  private readonly database: string;

  constructor(
    mongoDataApiUrl: string,
    {
      trpcClientFactory,
      dataSource,
      database,
    }: { trpcClientFactory?: () => TrpcClient; dataSource: string; database: string }
  ) {
    this.mongoDataApiUrl = mongoDataApiUrl;
    this.dataSource = dataSource;
    this.database = database;

    this.trpcClientFactory =
      trpcClientFactory ??
      (() =>
        createTRPCClient<AppRouter>({
          links: [httpBatchLinkWithPrefix(mongoDataApiUrl)],
        }));
  }

  get trpcClient(): TrpcClient {
    return this.trpcClientFactory();
  }

  async bulkWrite(mongoData: DocumentData) {
    await this.trpcClient.db.bulkWrite.mutate({
      database: this.database,
      dataSource: this.dataSource,
      data: mongoData,
    });
  }

  async update(path: string, mongoData: DocumentData) {
    await this.writeData(path, mongoData);
  }

  async set(path: string, mongoData: DocumentData, _options: SetOptions = {}) {
    await this.writeData(path, mongoData, { upsert: true });
  }

  private async writeData(path: string, mongoData: DocumentData, options: { upsert?: boolean; txId?: string } = {}) {
    await this.trpcClient.crud.updateOne.mutate({
      database: this.database,
      dataSource: this.dataSource,
      path,
      data: mongoData,
      ...options,
    });
  }

  async getCollection(collectionPath: CollectionPath, queryOptions: QueryOptions) {
    try {
      const documents = await this.trpcClient.crud.find.query({
        database: this.database,
        dataSource: this.dataSource,
        path: collectionPath,
        groupCollection: queryOptions.groupCollection,
        ...queryOptions,
      });

      return documents.map((docData) => (docData ? logEJSONparse(docData) : null));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching collection ${collectionPath}:`, err);
      throw err;
    }
  }

  async getDoc(path: string) {
    const document = await this.trpcClient.crud.findOne.query({
      database: this.database,
      dataSource: this.dataSource,
      path,
    });

    if (!document) {
      return;
    }

    return logEJSONparse(document);
  }

  async delete(path: string) {
    await this.trpcClient.crud.deleteOne.mutate({
      database: this.database,
      dataSource: this.dataSource,
      path,
    });
  }

  async runTransaction(): Promise<{ txId: string }> {
    const data = await this.trpcClient.db.startTx.mutate({ database: this.database, dataSource: this.dataSource });

    return data;
  }

  async commitTransactionCall(txId: string) {
    await this.trpcClient.db.commitTx.mutate({
      database: this.database,
      dataSource: this.dataSource,
      txId,
    });
  }

  async rollbackTransactionCall(txId: string) {
    await this.trpcClient.db.rollbackTx.mutate({
      database: this.database,
      dataSource: this.dataSource,
      txId,
    });
  }

  withTxId(txId: string): MongoClientImplClient {
    const injectTxId: TRPCLink<AppRouter> =
      () =>
      ({ op, next }) => {
        op.context = {
          ...op.context,
          txId,
        };
        return next(op);
      };

    const trpcClientFactory = () =>
      createTRPCClient<AppRouter>({
        links: [injectTxId, httpBatchLinkWithPrefix(this.mongoDataApiUrl)],
      });

    return new MongoClientImplClient(this.mongoDataApiUrl, {
      trpcClientFactory,
      dataSource: this.dataSource,
      database: this.database,
    });
  }
}

export class MongoClient {
  connectedClient: MongoClientImplClient | undefined = undefined;

  constructor(connectedClient?: MongoClientImplClient);

  constructor(mongoDataApiUrl: string, { dataSource, database }: { dataSource: string; database?: string });

  constructor(
    mongoDataApiUrl?: string | MongoClientImplClient,
    { dataSource, database }: { dataSource?: string; database?: string } = {}
  ) {
    if (mongoDataApiUrl instanceof MongoClientImplClient) {
      this.connectedClient = mongoDataApiUrl;
    } else if (mongoDataApiUrl && dataSource) {
      this.connectedClient = new MongoClientImplClient(mongoDataApiUrl, {
        dataSource,
        database: database ?? "default",
      });
    }
  }

  removeConnectedClient() {
    this.connectedClient = undefined;
  }

  setConnectedClient(connectedClient: MongoClientImplClient) {
    if (this.connectedClient) {
      throw new Error("MongoClient singleton already connected");
    }

    this.connectedClient = connectedClient;
  }

  protected connectedClientOrThrow() {
    if (!this.connectedClient) {
      throw new Error("MongoClient not connected");
    }

    return this.connectedClient;
  }

  doc<TModel extends DocumentData>(documentPath: string): MongoModelReference<TModel> {
    return new MongoModelReference<TModel>(this, convertDocumentPath(documentPath));
  }

  collection<TModel extends DocumentData>(collectionPath: CollectionPath): MongoCollectionReferenceModel<TModel> {
    return new MongoCollectionReferenceModel<TModel>(this, collectionPath);
  }

  collectionGroup<TModel extends DocumentData>(collectionName: CollectionName): MongoCollectionReferenceModel<TModel> {
    return new MongoCollectionReferenceModel<TModel>(this, buildCollectionPath(null, collectionName), {
      groupCollection: true,
    });
  }

  batch(): MongoModelWriteBatch {
    return new MongoModelWriteBatch(this);
  }

  async runTransaction(transaction: (transaction: MongoTransactionModel) => Promise<any>): Promise<any> {
    const connectedClient = this.connectedClientOrThrow();

    const { txId } = await connectedClient.runTransaction();

    const mongoTxClient = new MongoTxClient(connectedClient, txId);
    const tx = new MongoTransactionModelOps(mongoTxClient);

    try {
      await transaction(tx);
      await tx.commit();
    } catch (error) {
      await tx.rollback();
      throw error;
    }
  }

  async update(path: string, fieldOrData: string | Record<string, any>, value?: any) {
    const connectedClient = this.connectedClientOrThrow();

    const convertor = new ModelConverter(this);

    if (typeof fieldOrData === "string") {
      const data: any = {
        [fieldOrData]: value,
      };

      const mongoData = convertor.toMongoData(data, true);
      await connectedClient.update(path, mongoData);
      return;
    }

    const mongoData = convertor.toMongoData(fieldOrData, true);
    await connectedClient.update(path, mongoData);
  }

  async set(path: string, data: ReadonlyDeep<DocumentData>, options: SetOptions & { txId?: string } = {}) {
    const connectedClient = this.connectedClientOrThrow();

    const convertor = new ModelConverter(this);
    const mongoData = convertor.toMongoData(data, true);

    await connectedClient.set(path, mongoData, options);
  }

  async delete(path: string) {
    const connectedClient = this.connectedClientOrThrow();

    await connectedClient.delete(path);
  }

  async getDoc<TModel extends DocumentData>(path: DocumentPath): Promise<MongoDocumentSnapshotModel<TModel>> {
    const connectedClient = this.connectedClientOrThrow();

    const document = await connectedClient.getDoc(path);
    const convertor = new ModelConverter(this);

    return new MongoDocumentSnapshotModel<TModel>(
      this,
      path,
      document ? convertor.fromMongoData<TModel>(document) : undefined
    );
  }

  async getCollection(
    collectionPath: CollectionPath,
    queryOptions: QueryOptions
  ): Promise<Array<MongoQueryDocumentSnapshotModel<DocumentData>>> {
    const connectedClient = this.connectedClientOrThrow();

    const documents = await connectedClient.getCollection(collectionPath, queryOptions);

    const convertor = new ModelConverter(this);
    return documents.map((doc: any) => {
      const { _id: id, ...data } = doc;

      const path = `${collectionPath}/${id}` as DocumentPath;
      return new MongoQueryDocumentSnapshotModel(this, path, convertor.fromMongoData(data));
    });
  }

  async bulkWrite(ops: Record<CollectionName, Array<BulkWriteModel>>) {
    const connectedClient = this.connectedClientOrThrow();

    const convertor = new ModelConverter(this);

    await connectedClient.bulkWrite(convertor.toMongoData(ops, false));
  }
}

class MongoTxClient extends MongoClient {
  readonly txId: string;

  constructor(connectedClient: MongoClientImplClient, txId: string) {
    super(connectedClient);
    this.txId = txId;
  }

  async commitTransaction(txOps: [string, (db: MongoTxClient) => Promise<void>][]) {
    const connectedClientWithTx = this.connectedClientOrThrow().withTxId(this.txId);

    for (const [_, fn] of txOps) {
      await fn(this);
    }

    await connectedClientWithTx.commitTransactionCall(this.txId);
  }

  async rollbackTransaction(txId: string) {
    const connectedClient = this.connectedClientOrThrow();
    await connectedClient.rollbackTransactionCall(txId);
  }
}

class BaseDocumentSnapshotModel<TModel extends DocumentData> {
  constructor(
    private readonly apiClient: MongoClient,
    private readonly docPath: DocumentPath,
    protected readonly docData: WithModel<TModel> | undefined
  ) {}

  get<TFields extends ModelFields<TModel>, TPath extends keyof TFields>(fieldPath: TPath): TFields[TPath];

  get(ks: string): any {
    return get(this.docData, ks);
  }

  get id(): string {
    const parts = this.docPath.split("/");

    return parts[parts.length - 1];
  }

  exists(): boolean {
    return this.docData !== undefined;
  }

  get modelRef(): MongoModelReference<TModel> {
    return this.apiClient.doc(this.docPath);
  }

  get ref(): MongoModelReference<TModel> {
    return this.modelRef;
  }
}

export class MongoDocumentSnapshotModel<TModel extends DocumentData> extends BaseDocumentSnapshotModel<TModel> {
  asModelData(): WithModel<TModel> | undefined {
    return this.docData;
  }

  data(): WithModel<TModel> | undefined {
    return this.docData;
  }
}

export class MongoQueryDocumentSnapshotModel<TModel extends DocumentData> extends BaseDocumentSnapshotModel<TModel> {
  asModelData(): WithModel<TModel> {
    return this.docData as WithModel<TModel>;
  }

  data(): WithModel<TModel> {
    return this.docData as WithModel<TModel>;
  }
}

export type MongoDocumentSnapshotCallback<TModel extends DocumentData> = (
  snapshot: MongoDocumentSnapshotModel<TModel>
) => void | Promise<void>;

export type ModelTimestamp = MongoTimestamp;

type CombineInner<T, K extends PropertyKey = T extends unknown ? keyof T : never> = T extends unknown
  ? T & Partial<Record<Exclude<K, keyof T>, never>>
  : never;

type Combine<T> = { [K in keyof CombineInner<T>]: CombineInner<T>[K] };

type ConvertModelSingleType<T> = T extends ModelTimestamp
  ? MongoTimestamp
  : T extends Reference<infer TModel> | MongoModelReference<infer TModel>
    ? MongoModelReference<TModel>
    : T extends Array<infer U>
      ? Array<ConvertModelSingleType<U>>
      : T extends object
        ? { [K in keyof T]: ConvertModelSingleType<T[K]> }
        : T;

export type WithModel<TModel extends DocumentData> = ConvertModelSingleType<TModel>;

type ConvertModelObjectAddValue<T> = T extends ModelTimestamp | Timestamp
  ? FieldValue | ModelTimestamp | Date
  : T extends Reference<infer TModel extends DocumentData>
    ? FieldValue | Reference<TModel> | MongoModelReference<TModel>
    : T extends Array<infer U>
      ? FieldValue | Array<ConvertModelObjectAddValue<U>>
      : T extends object
        ? { [K in keyof T]: ConvertModelObjectAddValue<T[K]> }
        : FieldValue | T;

export type WithModelValue<TModel extends DocumentData> = ConvertModelObjectAddValue<Combine<TModel>>;

type FlattenWithPathsAndValue<T, Prefix extends string = ""> = {
  [K in keyof T & string]: IsAny<T[K]> extends true
    ? any
    : NonNullable<T[K]> extends Reference<any> | MongoModelReference<any> | ModelTimestamp | Date | Array<any>
      ? { [P in DotPrefix<K, Prefix>]?: ConvertModelObjectAddValue<T[K]> }
      : NonNullable<T[K]> extends object
        ?
            | { [P in DotPrefix<K, Prefix>]?: ConvertModelObjectAddValue<T[K]> }
            | FlattenWithPathsAndValue<NonNullable<T[K]>, DotPrefix<K, Prefix>>
        : { [P in DotPrefix<K, Prefix>]?: ConvertModelObjectAddValue<T[K]> };
}[keyof T & string];

export type FlattenWithPathsAndModel<T, Prefix extends string = ""> = {
  [K in keyof T & string]: IsAny<T[K]> extends true
    ? any
    : NonNullable<T[K]> extends Reference<any> | MongoModelReference<any> | ModelTimestamp | Date | Array<any>
      ? { [P in DotPrefix<K, Prefix>]: ConvertModelSingleType<T[K]> }
      : NonNullable<T[K]> extends object
        ?
            | { [P in DotPrefix<K, Prefix>]: ConvertModelSingleType<T[K]> }
            | FlattenWithPathsAndModel<NonNullable<T[K]>, DotPrefix<K, Prefix>>
        : { [P in DotPrefix<K, Prefix>]: ConvertModelSingleType<T[K]> };
}[keyof T & string];

type UnionToOptionalObject<T> = {
  [K in T extends infer U ? keyof U : never]?: T extends { [P in K]?: infer V } ? V : never;
};

export type UpdateFields<TModel extends DocumentData> = UnionToOptionalObject<
  FlattenWithPathsAndValue<NormalizeModel<OmitDeclFields<TModel>>>
>;

export type ModelFields<TModel extends DocumentData> = UnionToIntersection<
  FlattenWithPathsAndModel<NormalizeModel<OmitDeclFields<TModel>>>
>;

export type ListenSource = "default" | "cache";

export interface SnapshotListenOptions {
  readonly includeMetadataChanges?: boolean;
  readonly source?: ListenSource;
}
export class PathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PathError";
  }
}

export class MongoModelReference<TModel extends DocumentData> {
  private readonly documentId: DocumentId;

  private readonly documentPath: DocumentPath;

  private readonly apiClient: MongoClient;

  private readonly parentCollection: MongoCollectionReferenceModel<TModel>;

  constructor(apiClient: MongoClient, documentPath: DocumentPath) {
    const { documentId, parentPath, collectionName } = parseDocumentPath(documentPath);
    this.documentPath = documentPath;
    this.apiClient = apiClient;

    this.documentId = documentId;
    this.parentCollection = apiClient.collection<TModel>(buildCollectionPath(parentPath, collectionName));
  }

  toMongoRef(): MongoNativeReference {
    const { collectionName, fullDocumentId } = parseDocumentPath(this.path);

    return {
      $ref: collectionName,
      $id: fullDocumentId,
    };
  }

  collection<TSubCollectionModel extends string & SubCollectionIdOfTModel<TModel>>(
    collectionName: TSubCollectionModel
  ): MongoCollectionReferenceModel<SubCollectionOfTMModel<TModel, TSubCollectionModel>> {
    return new MongoCollectionReferenceModel<SubCollectionOfTMModel<TModel, TSubCollectionModel>>(
      this.apiClient,
      buildCollectionPath(this.path, convertCollectionName(collectionName))
    );
  }

  onSnapshotWithOptions(
    _options: SnapshotListenOptions | undefined,
    onNext: MongoDocumentSnapshotCallback<TModel>,
    onError?: ErrorCallback
  ): Unsubscribe {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({
      name: `listen doc ${this.path}`,
      op: "firestore",
    });

    const wrappedErrorCallback = (err: Error) => {
      span?.end();
      listenErrorCallbackSingleton(this.path, err, marker);
      if (onError) {
        onError(err);
      }
    };

    this.db
      .getDoc(this.path)
      .then((document) => {
        span?.end({ count: 1 });
        return onNext(document as MongoDocumentSnapshotModel<TModel>);
      })
      .catch(wrappedErrorCallback);

    // retutning stub unsubscribe
    return () => {};
  }

  onSnapshot(onNext: MongoDocumentSnapshotCallback<TModel>, onError?: ErrorCallback): Unsubscribe {
    return this.onSnapshotWithOptions({}, onNext, onError);
  }

  isEqual(other: MongoModelReference<DocumentData>): boolean {
    return this === other || this.path === other.path; // TODO: check also database and projectId
  }

  get db(): MongoClient {
    return this.apiClient;
  }

  get id(): string {
    return this.documentId;
  }

  get parent(): MongoCollectionReferenceModel<TModel> {
    return this.parentCollection;
  }

  get path(): DocumentPath {
    return this.documentPath;
  }

  parentModel(): MongoCollectionReferenceModel<TModel> {
    return this.parentCollection;
  }

  async get(): Promise<MongoDocumentSnapshotModel<TModel>> {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({ name: `get ${this.path}`, op: "mongo" });
    try {
      return await this.db.getDoc<TModel>(this.path);
    } catch (err: any) {
      listenErrorCallbackSingleton(this.path, err, marker);
      throw err;
    } finally {
      span?.end();
    }
  }

  async delete(): Promise<void> {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({ name: `delete ${this.path}`, op: "mongo" });
    try {
      await this.db.delete(this.path);
    } catch (err: any) {
      listenErrorCallbackSingleton(this.path, err, marker);
      throw err;
    } finally {
      span?.end();
    }
  }

  async update(data: UpdateFields<TModel>): Promise<void>;

  async update<TFields extends UpdateFields<TModel>, TPath extends keyof TFields>(
    field: TPath,
    value: TFields[TPath]
  ): Promise<void>;

  async update(fieldOrData: string | Record<string, any>, value?: any): Promise<void> {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({ name: `update ${this.path}`, op: "mongo" });
    try {
      await this.db.update(this.path, fieldOrData, value);
    } catch (err: any) {
      listenErrorCallbackSingleton(this.path, err, marker);
      throw err;
    } finally {
      span?.end();
    }
  }

  set(data: WithModelValue<ReadonlyDeep<TModel>>): Promise<void>;

  set(data: WithModelValue<Partial<ReadonlyDeep<TModel>>>, options: SetOptions): Promise<void>;

  async set(data: WithModelValue<Partial<ReadonlyDeep<TModel>>>, options: SetOptions = {}): Promise<void> {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({ name: `set ${this.path}`, op: "mongo" });
    try {
      await this.db.set(this.path, data, options);
    } catch (err: any) {
      listenErrorCallbackSingleton(this.path, err, marker);
      throw err;
    } finally {
      span?.end();
    }
  }

  narrow<TNarrow extends TModel>(): MongoModelReference<TNarrow> {
    return new MongoModelReference<TNarrow>(this.apiClient, this.documentPath);
  }
}

export class MongoDocumentChangeModel<TModel extends DocumentData> {
  constructor(
    readonly type: DocumentChangeType,
    readonly doc: MongoQueryDocumentSnapshotModel<TModel>,
    readonly oldIndex: number,
    readonly newIndex: number
  ) {}
}

type MongoQueryDocumentSnapshotCallback<TModel extends DocumentData> = (
  snapshot: MongoQueryDocumentSnapshotModel<TModel>,
  thisArg?: any
) => void;

export class MongoQuerySnapshotModel<TModel extends DocumentData> {
  constructor(private readonly mongoDocs: Array<MongoQueryDocumentSnapshotModel<TModel>>) {}

  get size(): number {
    return this.mongoDocs.length;
  }

  get empty(): boolean {
    return this.size === 0;
  }

  get docs(): Array<MongoQueryDocumentSnapshotModel<TModel>> {
    return this.mongoDocs;
  }

  isEqual(_other: MongoQuerySnapshotModel<TModel>): boolean {
    throw new Error("Method not implemented.");
  }

  get query() {
    throw new Error("Method not implemented.");
  }

  docChanges(): Array<MongoDocumentChangeModel<TModel>> {
    return this.mongoDocs.map((doc, i) => new MongoDocumentChangeModel("added", doc, -1, i));
  }

  forEach(callback: MongoQueryDocumentSnapshotCallback<TModel>, thisArg?: any): void {
    this.mongoDocs.forEach((doc) => {
      callback(doc, thisArg);
    });
  }
}

export type MongoQuerySnapshotCallback<TModel extends DocumentData> = (
  snapshot: MongoQuerySnapshotModel<TModel>
) => void;

type MongoWhere = any;

type QueryOptions = {
  limit?: number;
  orderBy?: { [field: string]: 1 | -1 };
  filter?: MongoWhere;
  originalFilters?: Array<QueryFilterConstraint>;
  groupCollection?: boolean;
};

const fieldReplacer = (field: FieldPath | string) => {
  if (typeof field === "string") {
    return field;
  }

  if (field.isEqual(FieldPath.documentId())) {
    return "documentId";
  }

  return JSON.stringify(field);
};

const replacer = (key: string, value: any) => {
  // If the key is an empty string, it means it's the root object
  if (value instanceof MongoModelReference) {
    return `Reference(${value.path})`;
  }

  if (value instanceof MongoTimestamp) {
    return `Timestamp(${value.toDate().toISOString()})`;
  }

  if (value instanceof Filter) {
    return `Filter(${JSON.stringify(value.queryKeys)})`;
  }

  return value;
};

export class MongoQueryModel<TModel extends DocumentData> implements WhereComposite<MongoQueryModel<TModel>> {
  protected readonly apiClient: MongoClient;

  protected readonly collectionPath: CollectionPath;

  protected readonly queryOptions: QueryOptions;

  constructor(apiClient: MongoClient, collectionPath: CollectionPath, queryOptions: QueryOptions = {}) {
    this.apiClient = apiClient;
    this.collectionPath = collectionPath;
    this.queryOptions = queryOptions;
  }

  get db(): MongoClient {
    return this.apiClient;
  }

  isEqual(other: MongoQueryModel<DocumentData>): boolean {
    return (
      this === other ||
      (this.collectionPath === other.collectionPath &&
        stableStringify(this.queryOptions) === stableStringify(other.queryOptions))
    );
  }

  where<TFields extends ModelFields<TModel>, TPath extends keyof TFields, TOperator extends WhereFilterOp>(
    fieldPathOrCompositeFilter: TPath | FieldPath | Filter<QueryCompositeFilterConstraint>,
    op?: TOperator,
    value?: ArrayOrValue<TOperator, TFields[TPath]>
  ): MongoQueryModel<TModel> {
    const originalFilters = [...(this.queryOptions.originalFilters ?? [])];

    if (fieldPathOrCompositeFilter instanceof Filter) {
      originalFilters.push(fieldPathOrCompositeFilter.queryCompositeFilterConstraint);
    } else {
      const fieldPath =
        fieldPathOrCompositeFilter instanceof FieldPath
          ? fieldPathOrCompositeFilter
          : new FieldPath(fieldPathOrCompositeFilter as string);
      const whereFilter = Filter.where()(fieldPath, op as WhereFilterOp, value);
      originalFilters.push(whereFilter.queryCompositeFilterConstraint);
    }

    return new MongoQueryModel<TModel>(this.apiClient, this.collectionPath, {
      ...this.queryOptions,
      filter: transformToMongoQuery(originalFilters),
      originalFilters,
    });
  }

  limit(limitResult: number): MongoQueryModel<TModel> {
    return new MongoQueryModel<TModel>(this.apiClient, this.collectionPath, {
      ...this.queryOptions,
      limit: limitResult,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  orderBy<TFields extends UpdateFields<TModel>, TPath extends keyof TFields>(
    fieldPath: TPath | keyof TModel,
    direction?: OrderByDirection
  ): MongoQueryModel<TModel>;

  orderBy(fieldPath: string, direction?: OrderByDirection): MongoQueryModel<TModel> {
    return new MongoQueryModel<TModel>(this.apiClient, this.collectionPath, {
      ...this.queryOptions,
      orderBy: { [fieldPath]: direction === "desc" ? -1 : 1 },
    });
  }

  onSnapshotWithOptions(
    _options: SnapshotListenOptions | undefined,
    onNext: MongoQuerySnapshotCallback<TModel>,
    onError?: ErrorCallback
  ): Unsubscribe {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({
      name: `listen query ${this.collectionPath}`,
      op: "firestore",
      attributes: { queryKeys: this.queryOptions },
    });

    const wrappedErrorCallback = (err: Error) => {
      span?.end();
      listenErrorCallbackSingleton(this.collectionPath, err, marker);
      if (onError) {
        onError(err);
      }
    };

    const { originalFilters, ...queryOptions } = this.queryOptions;

    this.db
      .getCollection(this.collectionPath, queryOptions)
      .then((documents) => {
        span?.end({ count: documents.length });
        const snapshot = new MongoQuerySnapshotModel<TModel>(documents as MongoQueryDocumentSnapshotModel<TModel>[]);
        onNext(snapshot);
      })
      .catch(wrappedErrorCallback);

    // retutning stub unsubscribe
    return () => {};
  }

  onSnapshot(onNext: MongoQuerySnapshotCallback<TModel>, onError?: ErrorCallback): Unsubscribe {
    return this.onSnapshotWithOptions({}, onNext, onError);
  }

  async get(): Promise<MongoQuerySnapshotModel<TModel>> {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({
      name: `query ${this.collectionPath}`,
      op: "mongo",
      attributes: { queryKeys: this.queryOptions },
    });

    try {
      const { originalFilters, ...queryOptions } = this.queryOptions;
      const docs = await this.db.getCollection(this.collectionPath, queryOptions);
      return new MongoQuerySnapshotModel<TModel>(docs as MongoQueryDocumentSnapshotModel<TModel>[]);
    } catch (err: any) {
      listenErrorCallbackSingleton(this.collectionPath, err, marker);
      throw err;
    } finally {
      span?.end();
    }
  }

  narrow<TNarrow extends DocumentData>(): MongoQueryModel<TNarrow> {
    return new MongoQueryModel<TNarrow>(this.apiClient, this.collectionPath, this.queryOptions);
  }
}

class MongoCollectionGroupModel<TModel extends DocumentData> extends MongoQueryModel<TModel> {}

export class Filter<TFilter extends QueryCompositeFilterConstraint | QueryConstraint = QueryConstraint> {
  protected constructor(
    private readonly queryFilterConstraint: TFilter,
    public readonly queryKeys: string[]
  ) {}

  get queryCompositeFilterConstraint(): TFilter {
    return this.queryFilterConstraint;
  }

  static where<TModel extends DocumentData>(): {
    <TFields extends ModelFields<TModel>, TPath extends keyof TFields, TOperator extends WhereFilterOp>(
      fieldPathOrCompositeFilter: TPath | FieldPath | Filter<QueryCompositeFilterConstraint>,
      op?: TOperator,
      value?: ArrayOrValue<TOperator, TFields[TPath]>
    ): Filter<QueryFieldFilterConstraint>;
  };

  static where() {
    return (fieldPath: FieldPath, opStr: WhereFilterOp, value: any): Filter<QueryFieldFilterConstraint> =>
      new Filter(where(fieldPath, opStr, value), [
        `where ${fieldReplacer(fieldPath)} ${opStr} ${JSON.stringify(value, replacer)}`,
      ]);
  }

  static or(...queryConstraints: Filter<QueryFilterConstraint>[]) {
    return new Filter(
      or(...queryConstraints.map((queryConstraint) => queryConstraint.queryCompositeFilterConstraint)),
      ["or", queryConstraints.map((qc) => qc.queryKeys).join(",")]
    );
  }

  static and(...queryConstraints: Filter<QueryFilterConstraint>[]) {
    return new Filter<QueryCompositeFilterConstraint>(
      and(...queryConstraints.map((queryConstraint) => queryConstraint.queryCompositeFilterConstraint)),
      ["and", queryConstraints.map((qc) => qc.queryKeys).join(",")]
    );
  }
}

interface WhereComposite<TResult> {
  where(compositeFilter: Filter<QueryCompositeFilterConstraint>): TResult;
}

export function randomBytes(nBytes: number): Uint8Array {
  const bytes = new Uint8Array(nBytes);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

const autoIdNewId = (): DocumentId => {
  // Alphanumeric characters
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  // The largest byte value that is a multiple of `char.length`.
  const maxMultiple = Math.floor(256 / chars.length) * chars.length;

  let autoId = "";
  const targetLength = 20;
  while (autoId.length < targetLength) {
    const bytes = randomBytes(40);
    for (let i = 0; i < bytes.length; ++i) {
      // Only accept values that are [0, maxMultiple), this ensures they can
      // be evenly mapped to indices of `chars` via a modulo operation.
      if (autoId.length < targetLength && bytes[i] < maxMultiple) {
        autoId += chars.charAt(bytes[i] % chars.length);
      }
    }
  }

  return autoId as DocumentId;
};

export class MongoCollectionReferenceModel<TModel extends DocumentData> extends MongoQueryModel<TModel> {
  constructor(apiClient: MongoClient, collectionPath: CollectionPath, queryOptions: QueryOptions = {}) {
    super(apiClient, collectionPath, queryOptions);
  }

  isEqual(other: MongoCollectionReferenceModel<any>): boolean {
    // TODO: very naive implementation check firestore github on how they do that
    return this.path === other.path;
  }

  doc<TDocId extends string & DocIdOfTModel<TModel>>(docId: TDocId) {
    return new MongoModelReference<DocModelOfTModel<TModel, TDocId>>(
      this.apiClient,
      buildDocumentPath(this.path, convertDocumentId(docId))
    );
  }

  private docFromId(documentPath: DocumentId): MongoModelReference<TModel> {
    return new MongoModelReference<TModel>(this.apiClient, buildDocumentPath(this.path, documentPath));
  }

  newDoc<TDocId extends string & DocIdOfTModel<TModel>>(): MongoModelReference<DocModelOfTModel<TModel, TDocId>> {
    const documentPath = autoIdNewId();
    return this.docFromId(documentPath) as MongoModelReference<any>;
  }

  async add(data: WithModelValue<OmitDeclFields<TModel>>): Promise<MongoModelReference<TModel>> {
    const newDocId = autoIdNewId();
    const path = buildDocumentPath(this.path, newDocId);
    await this.db.set(path, data);

    return new MongoModelReference<TModel>(this.apiClient, path);
  }

  get path(): CollectionPath {
    return this.collectionPath;
  }

  get id(): CollectionName {
    const { collectionName } = parseCollectionPath(this.path);
    return collectionName;
  }

  get parent(): MongoModelReference<any> | null {
    const { parentPath } = parseCollectionPath(this.path);

    if (!parentPath) {
      return null;
    }

    return new MongoModelReference<any>(this.apiClient, parentPath);
  }
}

type ListenErrorCallback = (path: string, err: Error, marker: Error) => void;

const globalMongoSingleton: MongoClient = new MongoClient();
let listenErrorCallbackSingleton: ListenErrorCallback;

export function clearMongoSingleton() {
  globalMongoSingleton.removeConnectedClient();
}

export const setModelsSingleton = (
  singleton: MongoClient | "default",
  { listenErrorCallback }: { listenErrorCallback?: () => void } = {}
) => {
  if (listenErrorCallback) {
    listenErrorCallbackSingleton = listenErrorCallback;
  } else {
    listenErrorCallbackSingleton = (path: string, err: Error, marker: Error) => {
      // eslint-disable-next-line no-console
      console.error(`listening on ${path}`, err, marker);
    };
  }

  if (singleton !== "default" && !singleton.connectedClient) {
    throw new Error("MongoClient singleton not connected");
  }

  if (singleton === "default") {
    setModelsSingleton(new MongoClient("/", { dataSource: "default", database: "default" }));
  } else {
    globalMongoSingleton.setConnectedClient(singleton.connectedClient!);
  }
};

function getDefaultClientIfNeeded(mongoInstance: MongoClient | undefined) {
  if (mongoInstance) {
    return mongoInstance;
  }

  return globalMongoSingleton;
}

export type ModelData<
  T extends DocumentData,
  IDField extends string | undefined = undefined,
  RefField extends string | undefined = undefined,
> = IDField extends string
  ? RefField extends string
    ? WithModel<T> & Record<IDField, string> & Record<RefField, MongoModelReference<T>>
    : WithModel<T> & Record<IDField, string>
  : RefField extends string
    ? WithModel<T> & Record<RefField, MongoModelReference<T>>
    : WithModel<T>;

export type IDOptions<
  IDField extends string | undefined = undefined,
  RefField extends string | undefined = undefined,
> = {
  idField?: IDField;
  refField?: RefField;
};

export type Model<TModel> = OmitDeclFields<TModel>;
export type ModelIdRef<TModel extends DocumentData> = ModelData<TModel, "id", "ref">;
export type ModelId<TModel extends DocumentData> = ModelData<TModel, "id">;
export type ModelRef<TModel extends DocumentData> = ModelData<TModel, undefined, "ref">;

export type ModelWithIdRef<TModel extends DocumentData> = TModel & ModelIdRef<TModel>;
export type ModelWithIdRefData<TModel extends DocumentData> = ModelIdRef<TModel> & {
  data: TModel;
};

export class MongoModelWriteBatch {
  private readonly apiClient: MongoClient;

  private ops: BulkDataRquest = {};

  constructor(apiClient: MongoClient) {
    this.apiClient = apiClient;
  }

  set<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: WithModelValue<OmitDeclFields<TModel>>
  ): MongoModelWriteBatch;

  set<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: WithModelValue<Partial<TModel>>,
    options: SetOptions
  ): MongoModelWriteBatch;

  set<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: WithModelValue<TModel> | WithModelValue<Partial<TModel>>,
    _options?: SetOptions
  ): this {
    const { collectionName } = parseDocumentPath(documentRef.path);

    const convert = new ModelConverter(this.apiClient);

    this.addOp(collectionName, {
      op: "updateOne",
      path: documentRef.path,
      data: convert.toMongoData(data, true),
      options: { upsert: true },
    });

    return this;
  }

  addOp(collectionName: CollectionName, op: BulkWriteModel) {
    if (!(collectionName in this.ops)) {
      this.ops[collectionName] = [];
    }

    this.ops[collectionName].push(op);
  }

  update<TModel extends DocumentData, TFields extends UpdateFields<TModel>, TPath extends keyof TFields>(
    documentRef: MongoModelReference<TModel>,
    fieldPath: TPath,
    value: TFields[TPath]
  ): MongoModelWriteBatch;

  update<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: UpdateFields<TModel>
  ): MongoModelWriteBatch;

  update<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    fieldOrData: UpdateFields<TModel>,
    value?: any
  ): this {
    const { collectionName } = parseDocumentPath(documentRef.path);

    const getData = () => {
      if (typeof fieldOrData === "string") {
        const data: any = {
          [fieldOrData]: value,
        };

        return data;
      }

      return fieldOrData;
    };

    const convertor = new ModelConverter(this.apiClient);

    this.addOp(collectionName, {
      op: "updateOne",
      path: documentRef.path,
      data: convertor.toMongoData(getData(), true),
      options: { upsert: false },
    });

    return this;
  }

  delete(documentRef: MongoModelReference<any>): MongoModelWriteBatch;

  delete<TModel extends DocumentData>(documentRef: MongoModelReference<TModel>): this {
    const { collectionName } = parseDocumentPath(documentRef.path);

    this.addOp(collectionName, { op: "deleteOne", path: documentRef.path });

    return this;
  }

  async commit(): Promise<void> {
    await this.apiClient.bulkWrite(this.ops);
  }
}

export class MongoTransactionModel {
  protected readonly txOps: Array<[string, (db: MongoTxClient) => Promise<void>]> = [];

  constructor(protected readonly apiClient: MongoTxClient) {}

  async get<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>
  ): Promise<MongoDocumentSnapshotModel<TModel>> {
    const marker = new Error();
    const span = getTracer().startInactiveSpan({ name: `get tx ${documentRef.path}`, op: "firestore" });
    try {
      return await this.apiClient.getDoc<TModel>(documentRef.path);
    } catch (err: any) {
      listenErrorCallbackSingleton(documentRef.path, err, marker);
      throw err;
    } finally {
      span?.end();
    }
  }

  delete<TModel extends DocumentData>(documentRef: MongoModelReference<TModel>): this {
    this.txOps.push(["delete", (db) => db.delete(documentRef.path)]);
    return this;
  }

  update<TModel extends DocumentData, TFields extends UpdateFields<TModel>, TPath extends keyof TFields>(
    documentRef: MongoModelReference<TModel>,
    fieldPath: TPath,
    value: TFields[TPath]
  ): MongoTransactionModel;

  update<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: UpdateFields<TModel>
  ): MongoTransactionModel;

  update<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    fieldOrData: UpdateFields<TModel> | (string & keyof TModel),
    value?: any
  ): this {
    this.txOps.push(["update", (db: MongoClient) => db.update(documentRef.path, fieldOrData as any, value)]);
    return this;
  }

  set<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: WithModelValue<OmitDeclFields<TModel>>
  ): this;

  set<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: WithModelValue<Partial<TModel>>,
    options: SetOptions
  ): this;

  set<TModel extends DocumentData>(
    documentRef: MongoModelReference<TModel>,
    data: WithModelValue<Partial<TModel>>,
    options?: SetOptions
  ): this {
    this.txOps.push(["set", (db: MongoClient) => db.set(documentRef.path, data, options as SetOptions)]);
    return this;
  }

  create<TModel extends DocumentData>(documentRef: MongoModelReference<TModel>, data: WithModelValue<TModel>): this {
    this.txOps.push(["create", (db: MongoClient) => db.set(documentRef.path, data)]);
    return this;
  }
}
class MongoTransactionModelOps extends MongoTransactionModel {
  async commit(): Promise<void> {
    await this.apiClient.commitTransaction(this.txOps);
  }

  async rollback(): Promise<void> {
    await this.apiClient.rollbackTransaction(this.apiClient.txId);
  }
}

export const runTransaction = <T>(
  updateFunction: (transaction: MongoTransactionModel) => Promise<T> | T,
  mongoInstance?: MongoClient
): Promise<T> => {
  const span = getTracer().startInactiveSpan({ name: "tx", op: "firestore" });
  const mongo = getDefaultClientIfNeeded(mongoInstance);

  return mongo.runTransaction(async (transaction) => {
    const result = updateFunction(transaction);
    if (result instanceof Promise) {
      return result.finally(() => {
        span?.end();
      });
    }
    span?.end();
    return Promise.resolve(result);
  });
};

export function addIdRef<
  TModel extends DocumentData,
  IDField extends string | undefined = undefined,
  RefField extends string | undefined = undefined,
>(
  modelData: WithModel<TModel>,
  doc: MongoQueryDocumentSnapshotModel<TModel>,
  options?: IDOptions<IDField, RefField>
): WithModel<TModel> & IDOptions<IDField, RefField> {
  if (options?.idField) {
    (modelData as any)[options.idField] = doc.id;
  }

  if (options?.refField) {
    (modelData as any)[options.refField] = doc.modelRef;
  }

  return modelData as WithModel<TModel> & IDOptions<IDField, RefField>;
}
export const modelFromPath = <TModel extends DocumentData>(
  path: string,
  mongoInstance?: MongoClient
): MongoModelReference<TModel> => {
  const mongoClient = getDefaultClientIfNeeded(mongoInstance);

  return mongoClient.doc(path);
};

export function getCollection<TModel extends DocumentData>(
  collectionType: TModelCtor<TModel>,
  mongoInstance?: MongoClient
): MongoCollectionReferenceModel<TModel> {
  const collectionName = getCollectionName(collectionType) as CollectionName;
  const mongoClient = getDefaultClientIfNeeded(mongoInstance);

  return mongoClient.collection<TModel>(buildCollectionPath(null, collectionName));
}

export function getCollectionGroup<TModel extends DocumentData>(
  collectionType: TModelCtor<TModel>,
  mongoInstance?: MongoClient
): MongoCollectionGroupModel<TModel> {
  const collectionName = getCollectionName(collectionType) as CollectionName;

  const mongoClient = getDefaultClientIfNeeded(mongoInstance);

  return mongoClient.collectionGroup<TModel>(collectionName);
}

export function getBatch(mongoInstance?: MongoClient): MongoModelWriteBatch {
  const mongoClient = getDefaultClientIfNeeded(mongoInstance);

  return mongoClient.batch();
}

export function docChangesToToArray<TModel extends DocumentData, TValue>(
  querySnapshot: MongoQuerySnapshotModel<TModel>,
  processValue: (docSnapshot: MongoQueryDocumentSnapshotModel<TModel>) => TValue,
  items: TValue[]
) {
  querySnapshot.docChanges().forEach((change) => {
    switch (change.type) {
      case "added":
        items.splice(change.newIndex, 0, processValue(change.doc));
        break;

      case "modified":
        items.splice(change.newIndex, 1, processValue(change.doc));
        break;

      case "removed":
        items.splice(change.oldIndex, 1);
        break;
    }
  });
}

// iterate query documents using a callback
export function forEachDoc<TModel extends DocumentData>(
  querySnapshot: MongoQuerySnapshotModel<TModel>,
  processValue: (docSnapshot: MongoQueryDocumentSnapshotModel<TModel>) => void
) {
  querySnapshot.docs.forEach((mongoDoc) => {
    processValue(mongoDoc);
  });
}

// map a query documents using a callback
export function mapDocsToArray<TModel extends DocumentData, TValue>(
  querySnapshot: MongoQuerySnapshotModel<TModel>,
  processValue: (docSnapshot: MongoQueryDocumentSnapshotModel<TModel>) => TValue
) {
  return querySnapshot.docs.map((mongoDoc) => processValue(mongoDoc));
}

const cache = {
  documents: new Map<string, MongoDocumentSnapshotModel<any>>(),
  namedQueries: new Map<string, MongoQuerySnapshotModel<any>>(),
};

export const loadBundle = (bundleString: any) => {
  try {
    const bundle = typeof bundleString === "string" ? JSON.parse(bundleString) : bundleString;

    const convertor = new ModelConverter(globalMongoSingleton);

    for (const [name, querySnapshot] of Object.entries(bundle.namedQueries)) {
      const docsMaps = querySnapshot as Record<string, MongoQuerySnapshotModel<any>>;
      const docsArray: MongoQueryDocumentSnapshotModel<any>[] = [];

      for (const [docPath, data] of Object.entries(docsMaps)) {
        docsArray.push(
          new MongoQueryDocumentSnapshotModel(
            globalMongoSingleton,
            convertDocumentPath(docPath),
            convertor.fromMongoData(data)
          )
        );
      }

      cache.namedQueries.set(name, new MongoQuerySnapshotModel<any>(docsArray));
    }

    // we must return a promise
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e as Error);
  }
};

const namedQuery = (name: string) => {
  const query = cache.namedQueries.get(name);
  if (!query) {
    return Promise.reject(new Error(`Named query "${name}" not found`));
  }

  // we must return a promise
  return Promise.resolve(query);
};

export const getDocsFromNamedQuery = async <TModel extends DocumentData>(
  name: string
): Promise<MongoQuerySnapshotModel<TModel>> => {
  const span = getTracer().startInactiveSpan({
    name: `cache ${name}`,
    op: "mongo",
  });

  try {
    return (await namedQuery(name)) as MongoQuerySnapshotModel<TModel>;
  } finally {
    span?.end();
  }
};

export const modelServerTimestamp = () => FieldValue.serverTimestamp();
export const modelDocumentId = () => FieldPath.documentId();
export const modelArrayUnion = (...elements: any[]) => FieldValue.arrayUnion(...elements);
export const modelArrayRemove = (...elements: any[]) => FieldValue.arrayRemove(...elements);
export const modelIncrement = (n: number) => FieldValue.increment(n);
export const modelDeleteField = () => FieldValue.delete();
